<template>
	<p v-if="!project_scope || !project_scope.length">
		No scope added
	</p>
	<div
		v-else
		class="project-scope-grid"
	>
		<div class="project-scope-grid__row">
			<div class="project-scope-grid__col">
				<p class="project-scope-grid__heading">Ref.</p>
			</div>
			<div class="project-scope-grid__col">
				<p class="project-scope-grid__heading">Date</p>
			</div>
			<div class="project-scope-grid__col project-scope-grid__col--3">
				<p class="project-scope-grid__heading">Description</p>
			</div>
			<div class="project-scope-grid__col">
				<p class="project-scope-grid__heading">Fee</p>
			</div>
		</div>
		<div class="project-scope-grid__row">
			<div class="project-scope-grid__col project-scope-grid__col--full-width">
				<div
					v-for="( scope, scope_index ) in project_scope"
					:key="'scope-' + scope_index"
					class="accordion"
					:class="{ 'accordion--expanded': scope.expanded }"
				>
					<button
						@click="expandScope( scope )"
						class="accordion__trigger"
					>
						<div class="project-scope-grid">
							<div class="project-scope-grid__row">
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__content">
										<a
											v-if="scope.pandadoc_id"
											class="external flex flex--inline flex--align-center flex--gap-medium"
											:href="'https://app.pandadoc.com/a/#/documents/' + scope.pandadoc_id"
											target="_blank"
										>
											<span class="u">{{ scope.sequence_prefix }}</span>
										</a>
										<span v-else>
											{{ scope.sequence_prefix }}
										</span>
									</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__content">{{ formatDate( scope.dateCreated ) }}</p>
								</div>
								<div class="project-scope-grid__col project-scope-grid__col--3">
									<p class="project-scope-grid__content">{{ scope.title }}</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__content">
										<span
											:class="{
												'error': scope.total <= 0,
												'green': scope.total > 0
											}"
										>
											{{ formatFee( scope.total ) }}
										</span>
									</p>
								</div>
							</div>
						</div>
					</button>
					<div class="accordion__content">
						<div class="project-scope-grid">
							<div class="project-scope-grid__row">
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__heading">Deliverable</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__heading">Project grid</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__heading">Price</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__heading">Price with discount</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__heading">Quantity</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__heading">Subtotal</p>
								</div>
							</div>
							<div
								v-for="( item, item_index ) in scope.scope_items"
								:key="'scope-' + scope_index + 'item-' + item_index"
								class="project-scope-grid__row project-scope-grid__row--compact"
							>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__content">{{ item.item_name }}</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__content">
										<router-link
											v-if="getMediaType( item )"
											class="u"
											:to="{
												name: 'projects__project',
												params: {
													project_id: route.params.project_id
												},
												query: {
													tab: 'grid',
													type: getMediaType( item ).slug
												}
											}"
										>
											{{ getMediaType( item ).title }}
										</router-link>
									</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__content">{{ formatFee( item.price_per_unit ) }}</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__content">{{ formatFee( item.price_per_unit_after_discount ) }}{{ item.discount_percentage ? '(' + item.discount_percentage + ')' : '' }}</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__content">{{ item.quantity }}</p>
								</div>
								<div class="project-scope-grid__col">
									<p class="project-scope-grid__content">{{ formatFee( item.total ) }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="project-scope-grid__row">
			<div class="project-scope-grid__col project-scope-grid__col--5">
				<p class="project-scope-grid__content"><strong>Total</strong></p>
			</div>
			<div class="project-scope-grid__col">
				<p
					class="project-scope-grid__content"
					:class="{ 'error': total <= 0 }"
				>
					<strong>{{ formatted_total }}</strong>
				</p>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router'
import moment from 'moment';
import { formatCurrency } from '../../helpers.js'

const route = useRoute();

const props = defineProps( {
 	project: {
		type: Object,
		required: true
	}
} );

const expanded_scopes = ref( [] );

const currency = computed( () => props.project.currency.length ? props.project.currency[0].title : 'USD' );

const project_scope = computed( () => {
	return props.project.project_scope.map( scope => {
		return {
			...scope,
			expanded: expanded_scopes.value.includes( scope.id )
		}
	} );
} );

const total = computed( () => {
	return project_scope.value.reduce( ( acc, val ) => acc + val.total, 0 );
} );

const formatted_total = computed( () => formatCurrency( total.value, currency.value ) )

function formatDate( date ) {
	return moment( date ).format( 'DD/MM/YYYY' )
}

function formatFee( fee ) {
	return formatCurrency( fee, props.project.currency.length ? props.project.currency[0].title : 'USD' );
}

function expandScope( scope ) {
	const found_id_index = expanded_scopes.value.indexOf( scope.id );
	found_id_index >= 0
		? expanded_scopes.value.splice( found_id_index, 1 )
		: expanded_scopes.value.push( scope.id )
	;
}

function getMediaType( scope_item ) {
	const pandadoc_sku = scope_item.pandadoc_sku?.[0];
	if ( !pandadoc_sku ) {
		return null;
	}
	return pandadoc_sku.media_type?.[0] ?? null;
}
</script>